import { ShieldCheck, CreditCard, ArrowRight, Sparkles } from 'lucide-react';

const SubscriptionStandaloneBanners = ({
  subscription,
  isTrialPendingVoucher,
  trialDaysRemaining,
  navigate
}) => {
  return (
    <>
      {/* Approved Plan Change / Custom Deal Banner */}
      {['approved', 'mandate_pending'].includes(subscription?.pending_change?.status) && (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-teal-900 via-[#067978] to-teal-800 p-5 sm:p-6 text-white shadow-xl shadow-teal-950/15 border border-teal-600/30 mb-6 animate-modal-entry">
          {/* Background decorative glows */}
          <div className="absolute -top-12 -right-12 w-44 h-44 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-emerald-400/15 rounded-full blur-xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0 shadow-inner">
                <ShieldCheck className="text-emerald-300" size={24} />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h4 className="text-base font-bold text-white tracking-tight">
                    {subscription?.pending_change?.custom_price ? 'Switch to Custom Plan' : 'Plan Change Approved by Trrip'}
                  </h4>
                </div>
                <p className="text-xs text-teal-100/90 mt-1 leading-relaxed max-w-xl">
                  {subscription?.pending_change?.custom_price ? (
                    <>
                      A custom plan has been configured for your agency (₹{subscription?.pending_change?.custom_price}/month + 18% GST). Click below to authorize your mandate and switch to your custom plan.
                    </>
                  ) : (
                    <>
                      Your switch to <b className="text-white font-bold">{subscription?.pending_change?.target_plan_id?.name || 'New Plan'} ({subscription?.pending_change?.target_billing_interval === 'year' ? 'Annual' : 'Monthly'})</b> is approved. Click below to authorize your new payment mandate to activate the plan.
                    </>
                  )}
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                const pending = subscription.pending_change;
                const targetPlanId = pending.target_plan_id?._id || pending.target_plan_id;
                const targetInterval = pending.target_billing_interval || 'month';
                const customPriceParam = pending.custom_price ? `&custom_price=${pending.custom_price}` : '';
                const customVoucherParam = pending.custom_voucher_limit ? `&custom_voucher_limit=${pending.custom_voucher_limit}` : '';
                const customTokenParam = pending.custom_link_token ? `&custom_link=${pending.custom_link_token}` : '';
                navigate(`/checkout?plan=${targetPlanId}&interval=${targetInterval}&from=subscription${customPriceParam}${customVoucherParam}${customTokenParam}`);
              }}
              className="px-5 py-2.5 bg-white hover:bg-teal-50 text-[#067978] text-xs font-black rounded-xl shadow-lg shadow-black/10 hover:shadow-xl transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer active:scale-95"
            >
              <CreditCard size={15} className="text-[#067978]" />
              <span>{subscription?.pending_change?.custom_price ? 'Switch to Custom Plan' : 'Authorize & Activate Plan'}</span>
              <ArrowRight size={14} className="text-[#067978]" />
            </button>
          </div>
        </div>
      )}

      {/* Active Custom Plan / Mandate Completed Banner */}
      {subscription?.plan_type === 'custom' && subscription?.status === 'active' && !subscription?.pending_change && (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#067978] via-teal-800 to-emerald-900 p-5 sm:p-6 text-white shadow-xl border border-teal-500/30 mb-6 animate-fadeIn">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0 shadow-inner">
                <ShieldCheck className="text-emerald-300" size={24} />
              </div>
              <div>
                <h4 className="text-base font-bold text-white tracking-tight">
                  Custom Plan Active &amp; Protected
                </h4>
                <p className="text-xs text-teal-100/90 mt-1 leading-relaxed max-w-xl">
                  Your custom agency deal (₹{subscription?.custom_price || subscription?.current_amount}/month + 18% GST) has been successfully authorized and active.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="px-3.5 py-1.5 bg-emerald-400/20 text-emerald-200 border border-emerald-300/30 text-xs font-semibold rounded-xl">
                ✓ Mandate Authorized &amp; Active
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Pending Activation 30-Day Free Trial Banner */}
      {subscription?.status === 'trial' && (!subscription?.trial_start || isTrialPendingVoucher) && (
        <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-white border border-teal-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-[#067978] border border-teal-100/80 flex items-center justify-center shrink-0 shadow-2xs">
              <Sparkles size={20} className="text-[#067978]" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h4 className="font-semibold text-sm sm:text-base text-slate-800 tracking-tight">30-Day Free Trial - Growth (Starts on 1st Voucher)</h4>
                <span className="px-2.5 py-0.5 rounded-full bg-teal-50 text-[#067978] border border-teal-100/70 text-[11px] font-medium">
                  30 Days · Starts on first voucher · Growth Plan Included
                </span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-500 mt-1 font-normal leading-relaxed">
                You have full trial access to explore the portal with all Growth tier features. Your 30-day trial period - growth will begin automatically when you create your first voucher. Select any plan below to authorize your mandate — <span className="text-[#067978] font-medium">you will not be charged until your trial concludes</span>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Active 30-Day Free Trial Banner */}
      {subscription?.status === 'trial' && subscription?.trial_start && trialDaysRemaining > 0 && (
        <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-white border border-teal-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-[#067978] border border-teal-100/80 flex items-center justify-center shrink-0 shadow-2xs">
              <Sparkles size={20} className="text-[#067978]" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h4 className="font-semibold text-sm sm:text-base text-slate-800 tracking-tight">30-Day Free Trial - Growth Active</h4>
                <span className="px-2.5 py-0.5 rounded-full bg-teal-50 text-[#067978] border border-teal-100/70 text-[11px] font-medium">
                  {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} left
                </span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-500 mt-1 font-normal leading-relaxed">
                You have full trial access with Growth plan features included. Select any plan below to authorize your mandate — <span className="text-[#067978] font-medium">you will not be charged until your 30-day trial period - growth ends</span>.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionStandaloneBanners;
